import { useCallback, useEffect, useState } from "react";

const KEY = "minion_favorites_v1";

export type FavType = "movie" | "news" | "review";

export interface FavItem {
  type: FavType;
  id: string;
  title: string;
}

function read(): FavItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items: FavItem[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* ignore quota errors */
  }
  window.dispatchEvent(new Event("favorites-changed"));
}

export function useFavorites() {
  const [items, setItems] = useState<FavItem[]>([]);

  useEffect(() => {
    const sync = () => setItems(read());
    sync();
    window.addEventListener("favorites-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("favorites-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const isFavorite = useCallback(
    (type: FavType, id: string) => items.some((i) => i.type === type && i.id === id),
    [items]
  );

  const toggleFavorite = useCallback((item: FavItem) => {
    const current = read();
    const exists = current.some((i) => i.type === item.type && i.id === item.id);
    const next = exists
      ? current.filter((i) => !(i.type === item.type && i.id === item.id))
      : [...current, item];
    write(next);
    return !exists;
  }, []);

  return { items, isFavorite, toggleFavorite };
}